import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { reject } from 'underscore';
import { ConnectorPickerModalComponent } from '../../components/modals/connector-picker-modal/connector-picker-modal.component';
import { BulkQuery } from '../../models/bulk-query';
import { Connector } from '../../models/connector';
import { ObjectActionListType, Permissions } from '../../models/permission';
import { ToggleItem } from '../../models/toggle-item';
import { CommonService } from '../../services/common/common.service';
import { XMLService } from '../../services/xml/xml.service';
import { KeyValueListComponent } from '../key-value-list/key-value-list.component';
import { MetadataBulkField, MetadataService, MetadataToken } from '../../services/metadata/metadata.service';
import { SettingsType } from '../../models/enums';

const OR = 'OR';

@Component({
    standalone: false,
    selector: 'res-bulk-search',
    templateUrl: './bulk-search.component.html',
    styleUrls: ['./bulk-search.component.scss']
})

export class BulkSearchComponent implements OnInit, AfterViewInit {
    @ViewChild(KeyValueListComponent, { static: true }) keyValueListComponent: KeyValueListComponent;

    @Input() isExportSettings = false;
    @Input() isBulkVisible = false;
    @Input() validateCallback: () => void;
    @Input() automationId = 'bulk-search';
    @Input() isDisabled = false;
    @Input() workflowMode = false;
    @Input() customTaskDescription: string;
    @Input() customizeClass = false;

    selectedConnector: Connector;
    hasConnectorPath = false;
    selectedConnectorPath = '';
    selectedFile: any;
    isValidConnectorFileName = false;
    currentDataAreaHandle = '';
    bulkQueryLimit = 5000;
    hasFileLargerThanLimit = false;
    selectedFileNameLargerThanLimit = '';

    searchOptions = [
        new ToggleItem({
            label: 'Include Families',
            isChecked: true,
        }),
        new ToggleItem({
            label: 'Include Metadata',
            isChecked: true,
        }),
        new ToggleItem({
            label: 'Expand Synonyms',
        }),
        new ToggleItem({
            label: 'Generate Reports',
        })
    ];

    isMetadataSearch: boolean;
    queries: Array<BulkQuery> = new Array<BulkQuery>({ name: '', hasError: false, errorText: null, isWarning: false });
    metadataFields: Array<MetadataBulkField> = null;
    metadataList: Array<MetadataToken> = [];
    selectedMetadataField: any;
    selectedCombineOperator: string;
    runAsCombinedSearch: boolean;
    isBulkSearchBusy: boolean;
    bulkSearchBusyMessage: string;

    uploadedFile: File = null;
    uploadedFileQueries: string[] = [];
    base64EncodedFileData: string = '';
    bulkSearchFilePath: string = null;

    showPopover: boolean;
    popoverMessage: string;
    popoverType: string;
    isBulkQueryValid = false;
    hasConnectorAccessPermissions = Permissions.checkUserPermission(ObjectActionListType.CONNECTOR_ACCESS);

    constructor(private commonService: CommonService, private xmlService: XMLService, private metadataService: MetadataService) { }

    ngOnInit(): void {
        if (!this.metadataFields) {
            this.metadataService.metadataBulkFields().then(metadataBulkFields => {
                this.metadataFields = metadataBulkFields;
            });
        }
        this.metadataService.tokenizationTypes().then(responseTokenizations => {
            this.metadataList = responseTokenizations;
        });
    }

    ngAfterViewInit(): void {
        if (this.workflowMode) {
            this.runAsCombinedSearch = true;
            this.selectedCombineOperator = OR;
            this.searchOptions[2].isCollapsed = true;
        }
    }

    init(queries: Array<string>, queriesDisabled?: boolean) {
        const newQueries = [];
        if (queries) {
            queries.forEach(xmlQuery => {
                let query = this.xmlService.xmlToQueryConverter(xmlQuery) as string;
                if (query) {
                    if (query.startsWith('(') && query.endsWith(')')) {
                        query = query.slice(1, -1);
                    }
                    newQueries.push(new BulkQuery({ name: query, hasError: false, errorText: null }));
                }
            });
        }
        if (!queriesDisabled) {
            newQueries.push({
                errorText: null,
                hasError: false,
                isWarning: false,
                name: ""
            });
        }
        this.queries = newQueries;
    }

    onIsMetadataChanged(): void {
        this.searchOptions[1].isDisabled = this.isMetadataSearch;
        this.searchOptions[2].isDisabled = this.isMetadataSearch;
        this.searchOptions[3].isDisabled = this.isMetadataSearch;

        if (this.isMetadataSearch) {
            this.resetMetadataFieldToDefault();
            this.searchOptions[1].isChecked = true;
            this.searchOptions[2].isChecked = false;
            this.searchOptions[3].isChecked = false;
            this.runAsCombinedSearch = false;
        }
        this.queriesChanged(null, "", true);
    }

    onSelectedMetadataFieldChanged(): void {
        this.clearRowErrors();
        this.queriesChanged();
    }

    clearRowErrors() {
        if (this.keyValueListComponent.errorIndices?.length > 0) {
            this.keyValueListComponent.errorIndices = [];
            if (this.queries?.length > 0) {
                this.queries.forEach(row => {
                    if (row.hasError) {
                        row.hasError = false;
                        row.errorText = null;
                        row.isWarning = false;
                    }
                });
            }
        }
    }

    onRunAsCombinedChanged(): void {
        if (this.runAsCombinedSearch && !this.selectedCombineOperator) {
            this.selectedCombineOperator = OR;
        }
    }

    resetMetadataFieldToDefault(): void {
        if (!this.selectedMetadataField && this.metadataFields?.length > 0) {
            this.selectedMetadataField = this.metadataFields[0];
        }
    }
    clearAllQueries(resetOptionsToDefault = false): void {
        this.isBulkQueryValid = false;
        this.queries.length = 0;
        this.queries = new Array<BulkQuery>({ name: '', hasError: false, errorText: null, isWarning: false });
        this.uploadedFile = null;
        if (resetOptionsToDefault) {
            this.resetMetadataFieldToDefault();
            this.isMetadataSearch = false;
            if (!this.workflowMode) {
                this.selectedCombineOperator = '';
                this.runAsCombinedSearch = false;
            } else {
                this.selectedCombineOperator = OR;
            }
        }
        this.closePopovers();
        this.hasConnectorPath = false;
        this.selectedConnectorPath = '';
        this.selectedConnector = null;
        this.hasFileLargerThanLimit = false;
        this.selectedFileNameLargerThanLimit = '';
    }

    localFileUpload($event): void {
        this.uploadedFile = $event.target.files[0];
        const fileReader: FileReader = new FileReader();

        this.isBulkQueryValid = false;
        this.queries = new Array<BulkQuery>();
        if (this.hasConnectorPath) {
            this.hasConnectorPath = false;
            this.selectedConnectorPath = '';
            this.selectedConnector = null;
        }
        this.hasFileLargerThanLimit = false;
        this.selectedFileNameLargerThanLimit = '';

        this.closePopovers();

        this.isBulkSearchBusy = true;
        this.bulkSearchBusyMessage = 'Loading bulk queries...';

        fileReader.onloadend = e => {
            const result = (<string>fileReader.result).split('\n');

            if (result.length > this.bulkQueryLimit) {
                this.base64EncodedFileData = this.returnBase64EncodedFileData(e?.target?.result);

                this.uploadedFileQueries = result.filter(query => query.trim() !== '');

                this.hasFileLargerThanLimit = true;
                this.selectedFileNameLargerThanLimit = this.uploadedFile?.name;

                this.isBulkSearchBusy = false;
            } else {
                const newEntries = [];
                result.forEach((entry, index) => {
                    return newEntries[index] = {
                        name: entry.trim(),
                        hasError: false,
                        errorText: ''
                    };
                });

                newEntries.forEach((entry) => {
                    this.queries.push(entry);
                });

                this.queries = reject(this.queries, function (entry) { return entry.name.trim() === ''; });

                if (this.queries.length < this.bulkQueryLimit) {
                    this.queries.push(new BulkQuery({ name: '', hasError: false, errorText: null }));
                }

                this.isBulkSearchBusy = false;
            }
        };

        fileReader.readAsText(this.uploadedFile);
    }

    isValidateEnabled(): boolean {
        const queryStrings = this.queries.filter(q => q.name.trim() !== '');
        return (queryStrings.length > 0 && (!this.isMetadataSearch || this.selectedMetadataField)) || (this.hasConnectorPath && this.selectedConnector) || (this.hasFileLargerThanLimit && this.uploadedFile);
    }

    getBulkSearchRequestData(isForValidation?: boolean, isForWorkflow?: boolean): any {
        const nonEmptyQueries = this.queries.filter(q => q.name.trim() !== '');
        this.queries = nonEmptyQueries.concat(new BulkQuery({ name: '', hasError: false, errorText: null }));
        const requestData = {
            attributes: [],
            expandFields: this.searchOptions[1]?.isChecked,  // include metadata option
            includeFamilyMembers: this.searchOptions[0]?.isChecked,
            includeSynonyms: isForWorkflow ? undefined : this.searchOptions[2]?.isChecked,
            reportRequests: isForValidation ? undefined : null,
            taskDescription: this.customTaskDescription ? this.customTaskDescription : isForValidation ? 'Validating Bulk Search Query' : 'Bulk Search in ',
            viewHandleList: [],
            fileName: this.uploadedFile?.name,
            produceSummaryReport: true,
            queries: []
        } as any;

        requestData.attributes.push({ name: 'targetHandle', value: '' });
        requestData.attributes.push({ name: 'targetName', value: '' });
        requestData.attributes.push({ name: 'includeFamily', value: requestData.includeFamilyMembers?.toString() });
        requestData.attributes.push({ name: 'includeMetadata', value: this.searchOptions[1]?.isChecked?.toString() });
        requestData.attributes.push({ name: 'includeSynonyms', value: requestData.includeSynonyms?.toString() });
        requestData.attributes.push({ name: 'searchtype', value: 'Bulk' });
        requestData.attributes.push({ name: 'AllMetadataChecked', value: 'false' });

        if (nonEmptyQueries.length > 0) {

            if (!isForValidation) {
                Object.assign({ reportRequests: requestData.produceSummaryReport ? [] : null }, requestData);
            }
            const xmlQueries = [];
            if (!isForValidation && this.runAsCombinedSearch && nonEmptyQueries.length > 1) {
                const queryStrings = nonEmptyQueries.map(q => q.name);
                const bulkSearchQuery = this.returnBulkSearchQueryForWorkflow(queryStrings, this.selectedCombineOperator);
                const xmlQuery = isForWorkflow ? bulkSearchQuery : this.xmlService.stringToXMLQuery(bulkSearchQuery);
                Object.assign(requestData, { query: xmlQuery });

                requestData.taskDescription = `(${bulkSearchQuery}) in `;
                delete requestData.fileName;
                delete requestData.queries;
                delete requestData.produceSummaryReport;
            } else {
                nonEmptyQueries.map(q => q.name).forEach((query) => {
                    let queryString = query.trim().replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ' ');
                    if (this.isMetadataSearch && this.selectedMetadataField) {
                        queryString = this.selectedMetadataField.name + (this.commonService.searchDialect === 'DR_CLASSIC' ? ':' : '::') +
                            '\"' + query.trim() + '\"';
                    }
                    xmlQueries.push(this.xmlService.stringToXMLQuery(queryString));
                    if (!isForValidation && isForWorkflow) {
                        Object.assign(requestData, { query: query });
                    }
                    if (!isForValidation && this.runAsCombinedSearch) {
                        const requestDataQuery = isForWorkflow ? queryString : xmlQueries[0];
                        Object.assign(requestData, { query: requestDataQuery });
                        requestData.taskDescription = `(${queryString}) in `;
                    }
                });
                requestData.queries = xmlQueries;
            }

            if (this.runAsCombinedSearch) {
                if (nonEmptyQueries.length >= 1) {
                    requestData.attributes.push({ name: 'searchDialect', value: this.commonService.searchDialect });
                    if (!isForValidation) {
                        delete requestData.fileName;
                        delete requestData.produceSummaryReport;
                        delete requestData.queries;
                    }
                }
            }

            return requestData;
        } else if (this.hasFileLargerThanLimit) {
            if (isForValidation) {
                Object.assign(requestData, {
                    base64EncodedFileData: this.base64EncodedFileData
                });
            } else {
                if (isForWorkflow) {
                    if (this.runAsCombinedSearch && this.uploadedFileQueries?.length > 1) {
                        const bulkSearchQuery = this.returnBulkSearchQueryForWorkflow(this.uploadedFileQueries, this.selectedCombineOperator);
                        Object.assign(requestData, { query: bulkSearchQuery });
                    }
                } else {
                    if (this.isExportSettings) {
                        if (this.bulkSearchFilePath) {
                            Object.assign(requestData, {
                                custodianQueriesFileName: this.bulkSearchFilePath
                            });
                        } else {
                            Object.assign(requestData, {
                                custodianQueriesFileData: this.base64EncodedFileData
                            });
                        }
                        delete requestData.bulkSearchFilePath;
                    } else {
                        if (this.runAsCombinedSearch) {
                            const bulkSearchCombinedQuery = this.returnBulkSearchQueryForWorkflow(this.uploadedFileQueries, this.selectedCombineOperator);
                            const xmlCombinedQuery = this.xmlService.stringToXMLQuery(bulkSearchCombinedQuery);
                            Object.assign(requestData, { query: xmlCombinedQuery });
                        } else {
                            Object.assign(requestData, {
                                bulkSearchFilePath: this.bulkSearchFilePath
                            });
                        }
                        delete requestData.fileName;
                        delete requestData.custodianQueriesFileName;
                        delete requestData.custodianQueriesFileData;
                    }
                    this.bulkSearchFilePath = null;
                }
            }
            delete requestData.queries;

            return requestData;
        } else if (this.hasConnectorPath) {
            const selectedConnectorNameIndex = this.selectedFile.handle.indexOf('/' + this.selectedFile.name);
            const selectedConnectorPath = this.selectedFile.handle.substring(0, selectedConnectorNameIndex);

            Object.assign(requestData, {
                connectorHandle: this.selectedConnector.handle,
                bulkSearchFileRelativePath: this.selectedFile.name,
                byMetadata: this.isMetadataSearch,
                bulkSearchFileDirectoryPath: selectedConnectorPath,
            });
            if (!isForValidation && this.runAsCombinedSearch) {
                // specific params for bulk search with path and run as combined search on as per ECASL-29347
                Object.assign(requestData, {
                    chunkQueries: true,
                    chunkSize: this.commonService.maxBooleanClauseCount,
                    combineClauseType: this.selectedCombineOperator
                });
            }
            delete requestData.fileName;
            delete requestData.queries;

            return requestData;
        }
        return null;
    }

    getMetadataSearchRequest(isForWorkflow?: boolean): any {
        const nonEmptyQueries = this.queries.filter(q => q.name.trim() !== '');
        const requestData = {
            fileName: this.uploadedFile?.name,
            includeFamily: this.searchOptions[0]?.isChecked,
            metaDataFieldName: this.selectedMetadataField?.name,
            targetViewHandle: '',
            taskDescription: `Metadata Bulk Search for ${this.selectedMetadataField?.displayName} in `,
            values: nonEmptyQueries.map(q => q.name)
        };
        if (isForWorkflow) {
            Object.assign(requestData, { metaDataField: this.selectedMetadataField?.displayName });
        }
        if (nonEmptyQueries.length > 0) {
            return requestData;
        } else if (this.hasFileLargerThanLimit) {
            requestData.values = this.uploadedFileQueries.map(q => q.replace(/\r/g, '').trim());
            return requestData;
        } else if (this.hasConnectorPath) {
            Object.assign(requestData, { bulkSearchFileRelativePath: this.selectedFile?.name });
            return requestData;
        }
        return null;
    }

    identify(index: number, item: any) {
        return item.handle;
    }

    showValidationResult(response): void {
        if (response && response.searchResponse) {
            if (response.numberOfErrors > 0 || response.numberOfWarnings > 0) {
                let numberOfErrorsAndWarnings = response.numberOfErrors + response.numberOfWarnings;
                let hasError = false;

                if (!this.hasConnectorPath && !this.hasFileLargerThanLimit) {
                    this.keyValueListComponent.errorIndices = [];
                    for (let i = 0; i < response.searchResponse.length; i++) {
                        const invalidMetadataResults = this.metadataService.findInvalidMetadataFields(response.searchResponse[i].query, this.metadataList, SettingsType.project);
                        const isDirtyMetadataInQuery = invalidMetadataResults.length > 0 ? true : false;
                        if (isDirtyMetadataInQuery && !response.searchResponse[i].extendedStatus) { numberOfErrorsAndWarnings += 1; }

                        if (response.searchResponse[i].extendedStatus || isDirtyMetadataInQuery) {
                            this.queries[i].hasError = true;
                            this.queries[i].isWarning = isDirtyMetadataInQuery ? false :
                                response.searchResponse[i].warning && response.searchResponse[i].extendedStatus.startsWith('Warning: ') ? true : false;
                            this.queries[i].errorText = isDirtyMetadataInQuery ? `Invalid metadata field \"${invalidMetadataResults[0].fullMatch}\"` : response.searchResponse[i].extendedStatus;
                            this.keyValueListComponent.errorIndices.push(i);
                            hasError = true;
                        } else {
                            this.queries[i].hasError = false;
                        }
                    }
                    if (hasError) {
                        this.keyValueListComponent.scrollToFirstError(true);
                    }
                }

                const err = numberOfErrorsAndWarnings === 1 ? 'query contains' : 'queries contain';
                this.popoverMessage = `${numberOfErrorsAndWarnings} ${err} warnings/errors  `;
                this.popoverType = 'error';
            } else {
                let hasError = false, numberOfErrors = 0;
                this.keyValueListComponent.errorIndices = [];

                for (let i = 0; i < this.queries.length; i++) {
                    const invalidMetadataResults = this.metadataService.findInvalidMetadataFields(this.queries[i].name, this.metadataList, SettingsType.project);

                    if (invalidMetadataResults.length > 0) {
                        const firstInvalid = invalidMetadataResults[0];
                        this.queries[i].hasError = true;
                        this.queries[i].errorText = `Invalid metadata field \"${firstInvalid.fullMatch}\"`;
                        this.keyValueListComponent.errorIndices.push(i);
                        hasError = true;
                        numberOfErrors += 1;
                    } else {
                        this.queries[i].hasError = false;
                        this.queries[i].isWarning = false;
                    }
                }

                if (hasError) {
                    const err = numberOfErrors === 1 ? 'query contains' : 'queries contain';
                    this.popoverMessage = `${numberOfErrors} ${err} warnings/errors  `;
                    this.popoverType = 'error';
                } else {
                    this.popoverMessage = 'All queries are valid';
                    this.popoverType = 'success';
                }
            }
            this.showPopover = true;
        }
    }

    showPreviousError() {
        this.keyValueListComponent.scrollToPreviousError();
    }

    showNextError() {
        this.keyValueListComponent.scrollToNextError();
    }

    maxRowsLimitReached(isFromPastedText?: boolean): void {
        this.showBulkQueryLimitWarning(isFromPastedText);
    }

    showBulkQueryLimitWarning(isFromPastedText?: boolean): void {
        let message = 'You have reached the Queries box limit.';
        if (!this.isExportSettings) {
            if (isFromPastedText) {
                message = `You have reached the Queries box limit of ${this.bulkQueryLimit} lines. Some lines were not pasted. To run all of the queries, perform the Bulk Search using a Connector File instead. If you do not have permissions to use a Connector File, contact your Administrator.`;
            } else {
                message += ' Perform the Bulk Search using a Connector File instead. If you do not have permissions to use a Connector File, contact your Administrator.';
            }
        }
        this.commonService.openWarningModal(message, 'Queries box limit reached');
    }

    queriesChanged(rowIndex?: number, eventType?: string, validateQueries?: boolean): void {
        this.isBulkQueryValid = false;
        if (["clearText", "removeRow", "keyup"].includes(eventType)) {
            const index = rowIndex ? this.keyValueListComponent.errorIndices?.indexOf(rowIndex) : this.keyValueListComponent.errorIndices?.indexOf(this.keyValueListComponent.currentFocusedIndex);
            if (index !== -1) {
                this.keyValueListComponent.errorIndices?.splice(index, 1);
            }
        }
        if (validateQueries) {
            this.keyValueListComponent.errorIndices = [];
            if (this.queries.length > 0) {
                for (let i = 0; i < this.queries.length; i++) {
                    this.queries[i].hasError = false;
                    this.queries[i].isWarning = false;
                    this.queries[i].errorText = '';
                }
            }
        }
        if (this.keyValueListComponent.errorIndices?.length > 0) {
            const err = this.keyValueListComponent.errorIndices.length === 1 ? 'query contains' : 'queries contain';
            this.popoverMessage = `${this.keyValueListComponent.errorIndices.length} ${err} warnings/errors  `;
        } else if (this.showPopover && (this.popoverType === 'error' || this.popoverType === 'success')) {
            this.popoverMessage = 'Please validate your modified queries';
            this.popoverType = 'warning';
        }
    }

    connectorFileClicked() {
        this.commonService.openModal(ConnectorPickerModalComponent, { class: 'vertical-percent-100 horizontal-percent-100' }).then(response => {
            if (response.modalData && response.modalData.selectedConnector && response.modalData.selectedFile) {
                this.clearAllQueries();
                this.hasConnectorPath = response.modalData.selectedConnector ? true : false;
                this.selectedConnector = response.modalData.selectedConnector;
                this.selectedFile = response.modalData.selectedFile;

                const remotePath = this.selectedConnector.remotePath;
                let selectedFileHandle = this.selectedFile.handle;
                const remotePathFolder = remotePath.substring(remotePath.lastIndexOf('/'), remotePath.length);
                const selectedHandleFolder = selectedFileHandle.substring(0, selectedFileHandle.indexOf('/', selectedFileHandle.indexOf('/') + 1));

                if (remotePathFolder === selectedHandleFolder) {
                    selectedFileHandle = selectedFileHandle.substring(remotePathFolder.length);
                }
                this.selectedConnectorPath = '//' + this.selectedConnector.networkId + remotePath + selectedFileHandle;
            }
        });
    }

    validateBulkQueries = (): void => {
        if (this.validateCallback) {
            this.validateCallback();
        }
    }

    showUpDownArrows() {
        return this.popoverType === 'error' && !this.hasConnectorPath && !this.hasFileLargerThanLimit;
    }

    closePopovers = (): void => {
        this.showPopover = false;
    }

    returnBase64EncodedFileData(data: any): string {
        // Convert input data to Uint8Array
        const utf8Encoder = new TextEncoder();
        const encodedData = utf8Encoder.encode(data);

        // Convert Uint8Array to binary string
        const binary = this.commonService.convertArrayToBinaryString(encodedData);

        // Convert binary string to base64
        return btoa(binary);
    }

    returnBulkSearchQueryForWorkflow(queryStrings: string[], combineOperator: string): string {
        const chunkSize = this.bulkQueryLimit;
        const chunks = [];

        for (let i = 0; i < queryStrings.length; i += chunkSize) {
            const chunk = queryStrings.slice(i, i + chunkSize).map(str => `(${str})`).join(` ${combineOperator} `);
            chunks.push(queryStrings.length > chunkSize ? `(${chunk})` : `${chunk}`);
        }

        return chunks.join(` ${combineOperator} `).replace(/\r?\n|\r/g, '').replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ' ');
    }
}
